// Quando um ciclo de fitas fecha sozinho — a MESMA conta no servidor e na tela.
//
// O estudo de calibração de 27/08/2026 mostrou que o fechamento manual precoce
// é uma das três causas de colheita no ciclo errado: em 09/05/2026 ciclos de
// nov/25 a jan/26 foram fechados de uma vez com a janela ainda aberta (previsto
// + tolerância caía entre 13/05 e 24/06), e as colheitas tardias desses ciclos
// caíram na fita SEGUINTE da mesma cor.
//
// Daí duas coisas usarem esta conta: o cron que fecha sozinho na data certa e o
// aviso do diálogo de fechar à mão. Se as duas divergirem, a tela promete uma
// data e o servidor recusa outra.
//
// 🔴 Datas CIVIS, comparadas como STRING. Nada de `Date` aqui: a Vercel roda em
// UTC e a fazenda em UTC-3, e entre 21:00 e 23:59 na Bahia o `new Date()` do
// servidor já está no dia seguinte — o ciclo fecharia um dia antes do prometido.
// ISO 8601 ordena lexicograficamente, então `<=` em string é a comparação certa.

import { somarDiasCivis } from "./data-fazenda.js";

/**
 * Data (civil, YYYY-MM-DD) em que o ciclo fecha sozinho: a ÚLTIMA previsão de
 * colheita entre os mangarás dele, mais a tolerância.
 *
 * A última, não a primeira: enquanto houver um mangará cuja janela ainda não
 * terminou, o ciclo pode receber colheita. `null` quando não há previsão
 * nenhuma — ciclo sem mangará não fecha sozinho (não há o que encerrar).
 */
export function dataFechamentoAutomatico(
  datasPrevistas: Array<string | null | undefined>,
  toleranciaDias: number,
): string | null {
  let ultima: string | null = null;
  for (const d of datasPrevistas) {
    if (!d) continue;
    const civil = d.slice(0, 10);
    if (ultima === null || civil > ultima) ultima = civil;
  }
  if (ultima === null) return null;
  return somarDiasCivis(ultima, toleranciaDias);
}

/**
 * A janela ainda está aberta em `hoje`? Aberta = o ciclo ainda pode receber
 * colheita, e fechar à mão agora é o erro de 09/05/2026 de novo.
 *
 * Sem data de fechamento (ciclo sem mangará) a janela NÃO conta como aberta:
 * não há nada a esperar, e travar o fechamento manual desse ciclo deixaria o
 * Hugo sem saída nenhuma.
 *
 * `hoje` vem de `dataHojeNaTz()` no servidor ou `hojeNaFazenda()` no client —
 * nunca de `new Date().toISOString()`.
 */
export function janelaAindaAberta(fechaEm: string | null, hoje: string): boolean {
  if (fechaEm === null) return false;
  return hoje <= fechaEm;
}

/**
 * Algum mangará do ciclo já passou da PRÓPRIA janela (previsão + tolerância)?
 * É a definição de "atrasado" nas telas do Cacho.
 *
 * ⚠️ Recebe as previsões dos MANGARÁS (`mangaras.data_prevista_colheita`), que
 * são cruas. NÃO passe `ciclo.dataPrevisaoInicio/Fim`: esses dois já vêm do
 * servidor com a tolerância somada (`getAllCiclos`), e somar de novo conta a
 * tolerância duas vezes — era o que o Dashboard fazia.
 *
 * Note que é o MENOR previsto que decide (basta UM mangará vencido), ao
 * contrário de `dataFechamentoAutomatico`, que usa o MAIOR porque o ciclo só
 * fecha quando o último mangará vence. Os dois estão certos; são perguntas
 * diferentes.
 */
export function algumMangaraVencido(
  datasPrevistas: Array<string | null | undefined>,
  toleranciaDias: number,
  hoje: string,
): boolean {
  for (const d of datasPrevistas) {
    if (!d) continue;
    if (somarDiasCivis(d.slice(0, 10), toleranciaDias) < hoje) return true;
  }
  return false;
}

/**
 * O ciclo está em janela de COLHEITA hoje? (filtro "Em andamento")
 *
 * Data civil, não semana ISO. A versão anterior comparava semanas ISO e
 * desistia quando o ano previsto era diferente do ano corrente
 * (`anoPrevisto !== currentWeek.year → false`) — o que escondia, o ano inteiro,
 * todo ciclo cuja colheita cai no ano seguinte. Em produção hoje isso atinge
 * `azul_prata_ago26` (previsto S01/2027, 571 cachos planejados), e atingiria
 * todos os cortes de nov/dez.
 */
export function cicloEmJanelaDeColheita(
  datasPrevistas: Array<string | null | undefined>,
  toleranciaDias: number,
  hoje: string,
): boolean {
  for (const d of datasPrevistas) {
    if (!d) continue;
    const prevista = d.slice(0, 10);
    const inicio = somarDiasCivis(prevista, -toleranciaDias);
    const fim = somarDiasCivis(prevista, toleranciaDias);
    if (hoje >= inicio && hoje <= fim) return true;
  }
  return false;
}
